import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { turmasService, Turma } from '../services/database'
import {
    PlusIcon,
    MagnifyingGlassIcon,
    TrashIcon,
    UsersIcon
} from '@heroicons/react/24/outline'

const TurmasPage = () => {
    const navigate = useNavigate()
    const [turmas, setTurmas] = useState<Turma[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')

    useEffect(() => {
        fetchTurmas()
    }, [])

    const fetchTurmas = async () => {
        try {
            setLoading(true)
            const data = await turmasService.getAll()
            setTurmas(data)
        } catch (error) {
            console.error('Erro ao carregar turmas:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation() // Prevent navigation
        if (window.confirm('Tem certeza que deseja excluir esta turma?')) {
            try {
                await turmasService.delete(id)
                setTurmas(prev => prev.filter(t => t.id !== id))
            } catch (error) {
                console.error('Erro ao excluir turma:', error)
                alert('Erro ao excluir turma. Verifique se existem dependências.')
            }
        }
    }

    const filteredTurmas = turmas.filter(t =>
        t.nome.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Turmas</h1>
                    <p className="text-gray-600">Gerencie as turmas e alunos</p>
                </div>
                <button
                    onClick={() => navigate('/turmas/new')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                >
                    <PlusIcon className="h-5 w-5" />
                    <span>Nova Turma</span>
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar turmas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTurmas.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-gray-500">
                        Nenhuma turma encontrada.
                    </div>
                ) : (
                    filteredTurmas.map((turma) => (
                        <div
                            key={turma.id}
                            className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow border border-gray-100"
                            onClick={() => navigate(`/turmas/${turma.id}`)}
                        >
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="p-3 rounded-full bg-blue-50 text-blue-600">
                                        <UsersIcon className="w-6 h-6" />
                                    </div>
                                </div>
                                <h3 className="font-bold text-lg text-gray-900 mb-2">{turma.nome}</h3>
                                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                                    {turma.descricao || 'Sem descrição'}
                                </p>
                                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-50">
                                    <span className="text-xs text-gray-400">
                                        Criado em {new Date(turma.created_at).toLocaleDateString('pt-BR')}
                                    </span>
                                    <button
                                        onClick={(e) => handleDelete(turma.id, e)}
                                        className="text-gray-400 hover:text-red-600 p-1"
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}

export default TurmasPage
